import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, ArrowRight, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { createAudioToken, generateAffirmationIdeas, saveAudioPhrases } from '../lib/api';
import {
  AFFIRMATION_AUDIO_INTRO,
  AFFIRMATION_AUDIO_OUTRO,
  normalizePhrases,
  validatePhrases,
} from '../lib/affirmationAudio';

const MIN_PHRASES = 3;
const MAX_PHRASES = 6;

export function StepAffirmationPhrases({
  submissionId,
  sessionId,
  phrases,
  onPhrasesChange,
  onAudioTokenChange,
  onAudioUrlChange,
  onNext,
  onBack,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [autoGenerationStatus, setAutoGenerationStatus] = useState('idle');
  const autoGenerationAttemptedRef = useRef(false);
  const currentPhrases = phrases?.length ? phrases : Array(MAX_PHRASES).fill('');

  useEffect(() => {
    const hasExistingPhrases = (phrases || []).some((phrase) => String(phrase || '').trim());

    if (hasExistingPhrases) {
      setAutoGenerationStatus((status) => (status === 'loading' ? status : 'ready'));
      return;
    }

    if (!submissionId || autoGenerationAttemptedRef.current) return;

    let cancelled = false;
    autoGenerationAttemptedRef.current = true;

    const generateInitialIdeas = async () => {
      setIsGeneratingIdeas(true);
      setAutoGenerationStatus('loading');

      try {
        const basePhrases = phrases?.length ? phrases : Array(MAX_PHRASES).fill('');
        const result = await generateAffirmationIdeas(submissionId, sessionId, basePhrases);
        const ideas = Array.isArray(result?.ideas) ? result.ideas.slice(0, MAX_PHRASES) : [];

        if (!ideas.length) {
          throw new Error('No se pudieron generar ideas para las frases.');
        }

        const next = Array.from({ length: MAX_PHRASES }).map((_, index) => {
          const existing = basePhrases[index] || '';
          return existing.trim() ? existing : (ideas[index] || '');
        });

        if (!cancelled) {
          onPhrasesChange(next);
          setAutoGenerationStatus('ready');
        }
      } catch (error) {
        if (!cancelled) {
          setAutoGenerationStatus('error');
          toast.error(error?.response?.data?.detail || error.message || 'No se pudieron generar ideas');
        }
      } finally {
        if (!cancelled) {
          setIsGeneratingIdeas(false);
        }
      }
    };

    generateInitialIdeas();

    return () => {
      cancelled = true;
    };
  }, [onPhrasesChange, phrases, sessionId, submissionId]);

  const updatePhrase = (index, value) => {
    const next = [...currentPhrases];
    next[index] = value;
    onPhrasesChange(next);
  };

  const handleSave = async () => {
    const validation = validatePhrases(currentPhrases);
    if (validation) {
      toast.error(validation);
      return;
    }
    if (!submissionId) {
      toast.error('Primero hay que guardar el formulario en el servidor.');
      return;
    }

    setIsSaving(true);
    try {
      const tokenResult = await createAudioToken(submissionId, sessionId);
      const cleanPhrases = normalizePhrases(currentPhrases).filter(Boolean);
      const audioState = await saveAudioPhrases(tokenResult.token, cleanPhrases);
      onPhrasesChange(audioState.phrases || cleanPhrases);
      onAudioTokenChange(tokenResult.token);
      onAudioUrlChange(tokenResult.audio_url);
      toast.success('Frases guardadas');
      onNext();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudieron guardar las frases');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardContent className="p-4 pb-0 sm:p-6 sm:pb-0">
          <div className="aspect-video overflow-hidden rounded-md border bg-black">
            <iframe
              src="https://player.vimeo.com/video/1000946542?badge=0&autopause=0&player_id=0&app_id=58479"
              title="6 - Frases de Afirmación."
              className="h-full w-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </CardContent>
        <CardHeader>
          <div className="space-y-1.5">
            <CardTitle>Técnica de reafirmación</CardTitle>
            <CardDescription>
              Redactá de 3 a 6 frases para moldear el texto que vas a grabar.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-md border bg-muted/40 p-4 space-y-3 text-sm">
            <p className="font-medium">El audio deberá comenzar así:</p>
            <p>{AFFIRMATION_AUDIO_INTRO}</p>
            <p className="font-medium">Después decís tus frases.</p>
            <p className="font-medium">Y la última frase repite esto:</p>
            <p>{AFFIRMATION_AUDIO_OUTRO}</p>
          </div>

          {autoGenerationStatus !== 'error' && (
            <div className="rounded-md border border-cyan-200 bg-cyan-50 p-3 text-sm text-cyan-950 dark:border-cyan-900/60 dark:bg-cyan-950/30 dark:text-cyan-100">
              <div className="flex items-start gap-2">
                {autoGenerationStatus === 'loading' && (
                  <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
                )}
                <p>
                  {autoGenerationStatus === 'loading'
                    ? 'Estamos generando frases orientativas en base a tus respuestas.'
                    : 'Estas frases fueron generadas automáticamente en base a tus respuestas a modo orientativo. Podés editarlas, reemplazarlas o borrarlas si querés.'}
                </p>
              </div>
            </div>
          )}

          <div className="grid gap-3">
            {Array.from({ length: MAX_PHRASES }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Label htmlFor={`affirmation-phrase-${index}`}>
                  Frase {index + 1}{index >= MIN_PHRASES ? ' (opcional)' : ''}
                </Label>
                <Textarea
                  id={`affirmation-phrase-${index}`}
                  value={currentPhrases[index] || ''}
                  onChange={(event) => updatePhrase(index, event.target.value)}
                  maxLength={280}
                  placeholder={`Escribí tu frase ${index + 1}. Ej: Soy constante con lo que me propongo.`}
                  rows={2}
                />
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onBack} disabled={isSaving || isGeneratingIdeas}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving || isGeneratingIdeas}>
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar frases y continuar
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default StepAffirmationPhrases;
